import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  TextField,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useLanguage } from '../contexts/LanguageContext';
import { getBackendUrl } from '../config/config';

/**
 * SearchBar component for serial number search
 * @param {Object} props - Component props
 * @param {Function} props.onSearch - Callback function when search is triggered
 * @param {boolean} props.disabled - Whether the search input is disabled
 */
const SearchBar = ({ onSearch, onClear, disabled = false }) => {
  const { t } = useLanguage();
  const [showManualFields, setShowManualFields] = useState(false);
  const [isScanMode, setIsScanMode] = useState(false);
  const [serialNumber, setSerialNumber] = useState('');
  const [articleCode, setArticleCode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const fileInputRef = useRef(null);

  const handleManualSearch = () => {
    if (serialNumber.trim() && articleCode.trim()) {
      onSearch(serialNumber.trim(), articleCode.trim());
    }
  };

  const handleManualKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleManualSearch();
    }
  };

  const handleCameraClick = () => {
    // Clear manual fields and switch to scan mode
    setSerialNumber('');
    setArticleCode('');
    setShowManualFields(false);
    setIsScanMode(true);
    if (onClear) onClear();
    fileInputRef.current?.click();
  };

  const handleManualToggle = () => {
    if (showManualFields && !isScanMode) {
      // Already in manual mode, hide fields and clear
      setSerialNumber('');
      setArticleCode('');
      setShowManualFields(false);
      setIsScanMode(false);
      if (onClear) onClear();
    } else {
      // Switch to manual mode - clear previous results and fields
      setSerialNumber('');
      setArticleCode('');
      setIsScanMode(false);
      setShowManualFields(true);
      if (onClear) onClear();
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input for future selections
    event.target.value = '';

    setIsScanning(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);

      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/vision/analyze`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success && data.serial) {
        // AI scan: search directly with serial only (no article needed)
        setSerialNumber(data.serial);
        setArticleCode('');
        setIsScanMode(true);
        setShowManualFields(true);
        
        setSnackbar({ 
          open: true, 
          message: `${t('scanSuccess')}: ${data.serial}`, 
          severity: 'success' 
        });
        // Trigger search with serial only
        onSearch(data.serial);
      } else {
        setSnackbar({ open: true, message: t('scanNoText'), severity: 'warning' });
      }
    } catch (error) {
      console.error('Vision API error:', error);
      setSnackbar({ open: true, message: t('scanError'), severity: 'error' });
    } finally {
      setIsScanning(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const canSearch = isScanMode 
    ? serialNumber.trim() 
    : (serialNumber.trim() && articleCode.trim());

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', mb: 4 }}>
      {/* Hidden file input for camera/upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
      />
      
      <Typography
        variant="h6"
        component="label"
        sx={{ mb: 3, display: 'block', color: 'text.secondary', textAlign: 'center' }}
      >
        {t('searchModeLabel')}
      </Typography>
      
      {/* Always visible buttons */}
      <Stack direction="row" spacing={3} justifyContent="center" sx={{ mb: showManualFields ? 3 : 0 }}>
        {/* Camera/AI Button */}
        <Button
          variant={isScanMode ? 'contained' : 'outlined'}
          size="large"
          onClick={handleCameraClick}
          disabled={disabled || isScanning}
          sx={{
            py: 3,
            px: 4,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            width: 200,
            height: 120,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 40 }}>
            {isScanning ? <CircularProgress size={40} color="inherit" /> : (
              <>
                <CameraAltIcon sx={{ fontSize: 40 }} />
                <AutoAwesomeIcon sx={{ fontSize: 20 }} />
              </>
            )}
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{isScanning ? t('scanningImage') : t('cameraAIButton')}</Typography>
        </Button>

        {/* Manual Search Button - toggles fields visibility */}
        <Button
          variant={(showManualFields && !isScanMode) ? 'contained' : 'outlined'}
          size="large"
          onClick={handleManualToggle}
          disabled={disabled}
          sx={{
            py: 3,
            px: 4,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            width: 200,
            height: 120,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 40 }}>
            <SearchIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{t('manualSearchButton')}</Typography>
        </Button>
      </Stack>

      {/* Manual search fields - shown when toggled */}
      {showManualFields && (
        <Stack spacing={2}>
          <TextField
            fullWidth
            label={t('serialNumberField')}
            placeholder={t('serialNumberPlaceholder')}
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            onKeyPress={handleManualKeyPress}
            disabled={disabled}
            required
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          
          {!isScanMode && (
            <TextField
              fullWidth
              label={t('articleCodeField')}
              placeholder={t('articleCodePlaceholder')}
              value={articleCode}
              onChange={(e) => setArticleCode(e.target.value)}
              onKeyPress={handleManualKeyPress}
              disabled={disabled}
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          )}
          
          <Button
            variant="contained"
            size="large"
            onClick={handleManualSearch}
            disabled={disabled || !canSearch}
            startIcon={<SearchIcon />}
            sx={{
              py: 1.5,
              borderRadius: 2,
            }}
          >
            {t('searchButton')}
          </Button>
        </Stack>
      )}
      
      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SearchBar;
